/**
 * Clocks, derived from the move log.
 *
 * The 2023 server kept `moveStartDate` and `timeForMoveMs` in memory and scheduled a
 * `setTimeout` to flag the player. Nothing was written down, so a restart lost every
 * clock in flight and every match with it — which is presumably why that build restarted
 * itself every 4.7 days on purpose and hoped nobody was playing.
 *
 * Here the clock is a *function of the log*. Each move carries the server time it
 * landed, so replaying the log recomputes both clocks exactly, and the player on move is
 * charged for the wall time since the last one. Downtime is charged the same way a
 * closed laptop is, because from the clock's point of view they are the same thing: real
 * time passed and no move arrived. Nothing needs to be stored beyond the timestamps, and
 * a restart is invisible.
 *
 * There is one deliberate exception, below: a match nobody has really started yet.
 *
 * This lives in `shared/` rather than the server because the client needs it too. A
 * client browsing back through a game has to show the clocks as they stood at that move,
 * and the only honest way to do that is to run the same function over a prefix of the
 * log. The 2023 client left the live clocks ticking under a historical position.
 */

import type { Variant } from '@gok/engine';
import type { MatchState, PlayedMove } from './wire.ts';

/**
 * Per-move allowance for network latency. The client's move is stamped when it reaches
 * the server, so the player pays for their own round trip; this gives a little of it
 * back. Server-authoritative, but generous by exactly this much.
 */
export const LAG_GRACE_MS = 200;

/**
 * A match where one side never moved is aborted rather than won: joining a game and
 * walking away should not hand out a rating point. Until both players have moved the
 * clocks do not run at all — this window is what the abort timer covers.
 */
export const ABORT_TIMEOUT_MS = 10 * 60_000;

/** Moves before which the clocks are not yet running (one from each player). */
const UNTIMED_PLIES = 2;

export interface ClockReading {
  readonly clocks: readonly [number, number];
  readonly turnStartedAt: number;
}

/**
 * Replay the timestamps in a log into a clock reading. Called on every move and, more
 * importantly, once per live match at boot.
 */
export function computeClocks(
  variant: Variant,
  moves: readonly PlayedMove[],
  startedAt: number,
): ClockReading {
  const clocks: [number, number] = [variant.timeInitialMs, variant.timeInitialMs];
  let turnStartedAt = startedAt;

  for (let ply = 0; ply < moves.length; ply++) {
    const at = moves[ply].at;
    if (ply >= UNTIMED_PLIES) {
      const player = ply % 2;
      const spent = Math.max(0, at - turnStartedAt - LAG_GRACE_MS);
      clocks[player] = clocks[player] - spent + variant.timeIncrementMs;
    }
    turnStartedAt = at;
  }

  return { clocks, turnStartedAt };
}

/** What the player on move has left, as of `now`. Negative means they have flagged. */
export function remaining(match: MatchState, now: number): number {
  if (match.moves.length < UNTIMED_PLIES) return match.clocks[match.moves.length % 2];
  const player = match.moves.length % 2;
  return match.clocks[player] - Math.max(0, now - match.turnStartedAt - LAG_GRACE_MS);
}

/**
 * When the current turn ends by itself, and how. `abort` while the match is still
 * notional, `flag` once the clocks are running.
 *
 * Returns a wall-clock instant, which may be in the past — at boot it usually is, and
 * the caller is expected to act on it immediately rather than schedule it.
 */
export function deadline(match: MatchState): { at: number; kind: 'abort' | 'flag' } {
  if (match.moves.length < UNTIMED_PLIES) {
    return { at: match.turnStartedAt + ABORT_TIMEOUT_MS, kind: 'abort' };
  }
  const player = match.moves.length % 2;
  return {
    at: match.turnStartedAt + match.clocks[player] + LAG_GRACE_MS,
    kind: 'flag',
  };
}

/** The clock reading after the player on move plays at `at`. */
export function afterMove(match: MatchState, at: number): ClockReading {
  const ply = match.moves.length;
  const clocks: [number, number] = [match.clocks[0], match.clocks[1]];

  if (ply >= UNTIMED_PLIES) {
    const player = ply % 2;
    const spent = Math.max(0, at - match.turnStartedAt - LAG_GRACE_MS);
    clocks[player] = clocks[player] - spent + match.variant.timeIncrementMs;
  }

  return { clocks, turnStartedAt: at };
}
